import { Button, Text } from '@/components/ui';
import { Table } from '@/components/ui/Table';
import { Select } from '@/components/ui/Select';
import { Loader } from '@/components/Loader';
import { RefreshCwIcon } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMonthlyConsumption } from '@/hooks/useMonthlyConsumption';
import { useContractOptions } from '@/hooks/useContractOptions';
import { useCompanies } from '@/hooks/useCompanies';
import { useTeamHoursStatistics } from '@/hooks/useTeamHoursStatistics';
import { MonthlyConsumption, TeamHoursStatistic } from '@/lib/api/monthly-consumption';
import { Pagination } from '@/components/ui/Pagination';
import { useSyncStore } from '@/lib/sync';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BarChart } from '@/components/charts/BarChart';

export function MonthlyConsumptionPage() {
  const { t } = useTranslation();
  const [selectedContractId, setSelectedContractId] = useState<string>('all');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const { lastTicketsSync } = useSyncStore();

  // Hooks
  const { contractOptions } = useContractOptions({
    includeInactive: true,
    includeAll: true,
    companyId: selectedCompanyId,
  }); // Include all contracts for monthly consumption filtering
  const { companies } = useCompanies({ limit: 100 });
  const { consumptions, isLoading, isRecalculating, pagination, refreshConsumptions } =
    useMonthlyConsumption({
      page: currentPage,
      limit: 10,
      contractId: selectedContractId === 'all' ? undefined : selectedContractId,
      companyId: selectedCompanyId === 'all' ? undefined : selectedCompanyId,
    });

  // Fetch team statistics when a company is selected
  const { teamStats } = useTeamHoursStatistics({
    contractId: selectedContractId === 'all' ? undefined : selectedContractId,
    companyId: selectedCompanyId === 'all' ? undefined : selectedCompanyId,
  });

  const handleRefresh = () => {
    refreshConsumptions();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const companyOptions = useMemo(
    () => [
      { value: 'all', content: t('common.all') },
      ...companies.map((company) => ({
        value: company.id,
        content: company.name,
      })),
    ],
    [companies, t]
  );

  // Reset contract selection when company changes
  useEffect(() => {
    setSelectedContractId('all');
  }, [selectedCompanyId]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedContractId, selectedCompanyId]);

  const formatMonth = (month: number) => {
    const monthNames = [
      t('common.monthsAbbrev.jan'),
      t('common.monthsAbbrev.feb'),
      t('common.monthsAbbrev.mar'),
      t('common.monthsAbbrev.apr'),
      t('common.monthsAbbrev.may'),
      t('common.monthsAbbrev.jun'),
      t('common.monthsAbbrev.jul'),
      t('common.monthsAbbrev.aug'),
      t('common.monthsAbbrev.sep'),
      t('common.monthsAbbrev.oct'),
      t('common.monthsAbbrev.nov'),
      t('common.monthsAbbrev.dec'),
    ];
    return monthNames[month - 1];
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-error-default font-medium';
    return 'text-success-default font-medium';
  };

  const columns: ColumnDef<MonthlyConsumption>[] = useMemo(
    () => [
      {
        id: 'company',
        header: t('monthlyConsumption.table.company'),
        cell: ({ row: { original } }) => original.contract?.company?.name || 'N/A',
      },
      {
        id: 'date',
        header: t('monthlyConsumption.table.date'),
        cell: ({ row: { original } }) => `${formatMonth(original.month)}. de ${original.year}`,
      },
      {
        id: 'hoursLimit',
        header: t('monthlyConsumption.table.hoursLimit'),
        cell: ({ row: { original } }) => `${original.hoursLimit}h/mês`,
      },
      {
        id: 'consumedHours',
        header: t('monthlyConsumption.table.consumedHours'),
        cell: ({ row: { original } }) => `${original.consumedHours}h`,
      },
      {
        id: 'remainingHours',
        header: t('monthlyConsumption.table.remainingHours'),
        cell: ({ row: { original } }) => {
          const remaining = Math.max(0, original.hoursLimit - original.consumedHours);
          return `${remaining}h`;
        },
      },
      {
        id: 'usage',
        header: t('monthlyConsumption.table.usage'),
        cell: ({ row: { original } }) => {
          const percentage =
            original.hoursLimit > 0
              ? Math.round((original.consumedHours / original.hoursLimit) * 100)
              : 0;
          return <span className={getUsageColor(percentage)}>{percentage}%</span>;
        },
      },
    ],
    [t, isRecalculating]
  );

  const teamStatsColumns: ColumnDef<TeamHoursStatistic>[] = useMemo(
    () => [
      {
        id: 'name',
        header: t('monthlyConsumption.teamStats.teamName'),
        cell: ({ row: { original } }) => original.name,
      },
      {
        id: 'totalHours',
        header: t('monthlyConsumption.teamStats.totalHours'),
        cell: ({ row: { original } }) => `${original.totalHours}h`,
      },
      {
        id: 'ticketCount',
        header: t('monthlyConsumption.teamStats.ticketCount'),
        cell: ({ row: { original } }) => original.ticketCount,
      },
    ],
    [t]
  );

  // Prepare chart data
  const chartData = useMemo(() => {
    if (!consumptions || consumptions.length === 0) return [];

    // Sort by year and month
    const sortedConsumptions = [...consumptions].sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.month - b.month;
    });

    return sortedConsumptions.map((consumption) => ({
      label: `${formatMonth(consumption.month)}/${consumption.year}`,
      value: consumption.consumedHours,
    }));
  }, [consumptions]);

  return (
    <div className="gap-2xl flex flex-1 flex-col">
      <Loader isLoading={isLoading} />

      <header className="flex w-full items-center justify-between">
        <Text as="h1" variant="heading">
          {t('monthlyConsumption.title')}
        </Text>
        <div className="gap-md flex items-center">
          {lastTicketsSync && (
            <Text variant="label" size="sm" className="text-neutral-muted">
              {t('monthlyConsumption.lastTicketsSync')}{' '}
              {formatDistanceToNow(new Date(lastTicketsSync), { addSuffix: true, locale: ptBR })}
            </Text>
          )}
          <Button variant="secondary" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCwIcon size={20} />
            {t('common.refresh')}
          </Button>
        </div>
      </header>

      <div>
        <div className="gap-xs mb-4 flex items-center">
          <Select
            options={companyOptions}
            placeholder={t('monthlyConsumption.filter.company')}
            width={350}
            value={selectedCompanyId === 'all' ? '' : selectedCompanyId}
            onValueChange={(value) => setSelectedCompanyId(value || 'all')}
          />
          <Select
            options={contractOptions}
            placeholder={t('monthlyConsumption.filter.contract')}
            width={350}
            value={selectedContractId === 'all' ? '' : selectedContractId}
            onValueChange={(value) => setSelectedContractId(value || 'all')}
          />
        </div>

        <Table data={consumptions} columns={columns} />
        <Pagination
          className="mt-lg"
          currentPage={pagination.page}
          totalPages={pagination.totalPages}
          onPageChange={handlePageChange}
          disabled={isLoading}
        />

        {/* Consumption Chart */}
        {selectedCompanyId !== 'all' && chartData.length > 0 && (
          <div className="bg-base-white border-neutral-subtle mt-2xl p-lg rounded-md border">
            <Text as="h2" variant="label" size="lg" strong className="mb-md text-center">
              {t('monthlyConsumption.chart.title')}
            </Text>
            <BarChart data={chartData} unit="h" />
          </div>
        )}

        {/* Team Statistics Table */}
        {selectedCompanyId !== 'all' && teamStats.length > 0 && (
          <div className="mt-2xl">
            <Text as="h2" variant="label" size="lg" strong className="mb-lg">
              {t('monthlyConsumption.teamStats.title')}
            </Text>
            <Table data={teamStats} columns={teamStatsColumns} />
          </div>
        )}
      </div>
    </div>
  );
}
